import { Injectable } from '@angular/core';
import { asyncScheduler, BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { GoogleUser } from '../../shared/models/auth.model';


@Injectable({
    providedIn: 'root',
})
export class GoogleSignInService {
    userSubject = new BehaviorSubject<GoogleUser | null>(null);
    googleUser$ = this.userSubject.asObservable();

    private scriptLoaded = false;

    async initialize(): Promise<void> {
        if (!this.scriptLoaded) {
            await this.loadGoogleScript();
            this.scriptLoaded = true;
        }

        (window).google.accounts.id.initialize({
            client_id: environment.googleClientId,
            callback: this.handleCredentialResponse.bind(this),
            auto_select: true,
        });

        (window).google.accounts.id.prompt((notification: google.accounts.id.PromptMomentNotification) => {
            if (notification.isNotDisplayed()) {
                console.log('Not displayed: maybe user not eligible.');
            }
            if (notification.isSkippedMoment()) {
                console.log('Skipped: user closed it or not signed in.');
            }
            if (notification.getMomentType() === 'display') {
                console.log('Prompt displayed.');
            }
        });
    }

    renderButton(elementId: string): void {
        const element = document.getElementById(elementId);
        if (element) {
            google.accounts.id.renderButton(element, {
                theme: 'outline',
                size: 'large',
                text: 'continue_with',
                type: 'standard',
                shape: 'circle',
            });
        } else {
            console.warn(`Element with id "${elementId}" not found.`);
        }
    }

    signOut(): void {
        google.accounts.id.disableAutoSelect();
        this.userSubject.next(null);
    }

    loadGoogleScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            const existingScript = document.getElementById('google-client-script');
            if (existingScript) {
                this.waitForGoogle(resolve, reject);
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.id = 'google-client-script';
            script.async = true;
            script.defer = true;
            script.onload = () => this.waitForGoogle(resolve, reject);
            script.onerror = () => reject(new Error('Failed to load Google Identity script'));
            document.head.appendChild(script);
        });
    }

    private waitForGoogle(resolve: () => void, _: () => void): void {
        const check = () => {
            if ((window).google?.accounts?.id) {
                resolve();
            } else {
                asyncScheduler.schedule(check, 50);
            }
        };
        check();
    }

    private handleCredentialResponse(response: google.accounts.id.CredentialResponse): void {
        const credential = response.credential;
        const payload = this.decodeJwt(credential);
        this.userSubject.next(payload);
    }

    private decodeJwt(token: string): GoogleUser {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        const parsedPayload = jsonPayload && jsonPayload !== '' ? JSON.parse(jsonPayload) : {};
        parsedPayload.token = token;

        return parsedPayload;
    }
}
